import { useState, useEffect, useRef } from 'react';
import './App.css';
import { AuthProvider, useAuth } from './AuthContext';
import { LanguageProvider } from './LanguageContext';
import Header from './components/Header';
import CategoryPage from './components/CategoryPage';
import CategoryRecipeList from './components/CategoryRecipeList';
import RecipePage from './components/RecipePage';
import LoginPage from './components/LoginPage';
import FloatingAddButton from './components/FloatingAddButton';
import ImportPanel from './components/ImportPanel';
import ShoppingList from './components/ShoppingList';
import MealPlanner from './components/MealPlanner';
import EbookExportModal from './components/EbookExportModal';
import CollectionsPage from './components/CollectionsPage';
import CollectionRecipeList from './components/CollectionRecipeList';
import SwipeView from './components/SwipeView';
import AboutPage from './components/AboutPage';

function AppContent() {
  const { loading } = useAuth();
  // view: 'categories' | 'category' | 'recipe' | 'login' | 'shopping' | 'meal-plan' | 'favourites' | 'collections' | 'collection' | 'swipe' | 'visitor-picks' | 'about'
  const [view, setView] = useState('categories');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedRecipeId, setSelectedRecipeId] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [, setShowLogin] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showEbookExport, setShowEbookExport] = useState(false);
  const swipeStateRef = useRef(null); // persists swipe position across navigation

  // Set up browser history so back button works + handle deep links
  useEffect(() => {
    // Parse current URL path for deep links (e.g. /recipe/4)
    const path = window.location.pathname;
    const recipeMatch = path.match(/^\/recipe\/(\d+)$/);
    if (recipeMatch) {
      const id = parseInt(recipeMatch[1], 10);
      setSelectedRecipeId(id);
      setView('recipe');
      window.history.replaceState({ view: 'recipe', id }, '', path);
    } else {
      window.history.replaceState({ view: 'categories' }, '', '/');
    }

    const handlePopState = (e) => {
      const state = e.state;
      if (!state || state.view === 'categories') {
        setView('categories');
        setSelectedCategory(null);
        setSelectedRecipeId(null);
        setShowLogin(false);
      } else if (state.view === 'category') {
        setView('category');
        setSelectedCategory(state.category);
        setSelectedRecipeId(null);
        setShowLogin(false);
      } else if (state.view === 'recipe') {
        setView('recipe');
        setSelectedRecipeId(state.id);
        setShowLogin(false);
      } else if (state.view === 'login') {
        setView('login');
        setShowLogin(true);
        setSelectedRecipeId(null);
        setSelectedCategory(null);
      } else if (state.view === 'shopping') {
        setView('shopping');
        setShowLogin(false);
        setSelectedRecipeId(null);
        setSelectedCategory(null);
      } else if (state.view === 'meal-plan') {
        setView('meal-plan');
        setShowLogin(false);
        setSelectedRecipeId(null);
        setSelectedCategory(null);
      } else if (state.view === 'all-recipes') {
        setView('all-recipes');
        setSelectedRecipeId(null);
        setSelectedCategory(null);
      } else if (state.view === 'favourites') {
        setView('favourites');
        setSelectedRecipeId(null);
        setSelectedCategory(null);
      } else if (state.view === 'collections') {
        setView('collections');
        setSelectedRecipeId(null);
        setSelectedCategory(null);
      } else if (state.view === 'collection') {
        setView('collection');
        setSelectedCollection(state.collection);
      } else if (state.view === 'swipe') {
        setView('swipe');
        setSelectedRecipeId(null);
        setSelectedCategory(null);
      } else if (state.view === 'visitor-picks') {
        setView('visitor-picks');
        setSelectedRecipeId(null);
        setSelectedCategory(null);
      } else if (state.view === 'about') {
        setView('about');
        setSelectedRecipeId(null);
        setSelectedCategory(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleSelectCategory = (category) => {
    window.history.pushState({ view: 'category', category }, '', '/');
    setSelectedCategory(category);
    setView('category');
  };

  const handleSelectRecipe = (id) => {
    window.history.pushState({ view: 'recipe', id }, '', `/recipe/${id}`);
    setSelectedRecipeId(id);
    setView('recipe');
  };

  const handleShowLogin = () => {
    window.history.pushState({ view: 'login' }, '', '/');
    setShowLogin(true);
    setView('login');
  };

  const handleShowShopping = () => {
    window.history.pushState({ view: 'shopping' }, '', '/');
    setView('shopping');
  };

  const handleShowMealPlan = () => {
    window.history.pushState({ view: 'meal-plan' }, '', '/');
    setView('meal-plan');
  };

  const handleShowAllRecipes = () => {
    window.history.pushState({ view: 'all-recipes' }, '', '/');
    setView('all-recipes');
  };

  const handleShowFavourites = () => {
    window.history.pushState({ view: 'favourites' }, '', '/');
    setView('favourites');
  };

  const handleShowCollections = () => {
    window.history.pushState({ view: 'collections' }, '', '/');
    setView('collections');
  };

  const handleSelectCollection = (collection) => {
    window.history.pushState({ view: 'collection', collection }, '', '/');
    setSelectedCollection(collection);
    setView('collection');
  };

  const handleShowSwipe = () => {
    swipeStateRef.current = null; // clear so SwipeView reshuffles on fresh entry
    window.history.pushState({ view: 'swipe' }, '', '/');
    setView('swipe');
  };

  const handleShowVisitorPicks = () => {
    window.history.pushState({ view: 'visitor-picks' }, '', '/');
    setView('visitor-picks');
  };

  const handleShowAbout = () => {
    window.history.pushState({ view: 'about' }, '', '/');
    setView('about');
  };

  const handleHome = () => {
    window.history.pushState({ view: 'categories' }, '', '/');
    setView('categories');
    setSelectedCategory(null);
    setSelectedRecipeId(null);
    setShowLogin(false);
  };

  const handleBack = () => {
    window.history.back();
  };

  if (loading) {
    return (
      <div className="app">
        <Header onShowLogin={handleShowLogin} onShowShopping={handleShowShopping} onShowMealPlan={handleShowMealPlan} onShowCollections={handleShowCollections} onHome={handleHome} onShowEbookExport={() => setShowEbookExport(true)} onShowSwipe={handleShowSwipe} onShowAbout={handleShowAbout} />
      </div>
    );
  }

  function handleRecipeSaved(recipeId) {
    handleSelectRecipe(recipeId);
  }

  return (
    <div className="app">
      <Header onShowLogin={handleShowLogin} onShowShopping={handleShowShopping} onShowMealPlan={handleShowMealPlan} onShowCollections={handleShowCollections} onHome={handleHome} onShowEbookExport={() => setShowEbookExport(true)} onShowSwipe={handleShowSwipe} onShowAbout={handleShowAbout} />

      <div
        key={`${view}-${selectedRecipeId || 0}-${selectedCategory?.id || 0}-${selectedCollection?.id || 0}`}
        className="view-transition"
      >
        {view === 'swipe' ? (
          <SwipeView onBack={handleBack} onSelectRecipe={handleSelectRecipe} stateRef={swipeStateRef} onShowFavourites={handleShowFavourites} />
        ) : view === 'meal-plan' ? (
          <MealPlanner onBack={handleBack} onSelectRecipe={handleSelectRecipe} />
        ) : view === 'shopping' ? (
          <ShoppingList onBack={handleBack} />
        ) : view === 'login' ? (
          <LoginPage onBack={handleBack} />
        ) : view === 'recipe' ? (
          <RecipePage recipeId={selectedRecipeId} onBack={handleBack} />
        ) : view === 'all-recipes' ? (
          <CategoryRecipeList
            category={null}
            allRecipesOnly={true}
            onSelectRecipe={handleSelectRecipe}
            onBack={handleBack}
          />
        ) : view === 'favourites' ? (
          <CategoryRecipeList
            category={null}
            favouritesOnly={true}
            onSelectRecipe={handleSelectRecipe}
            onBack={handleBack}
          />
        ) : view === 'collections' ? (
          <CollectionsPage onSelectCollection={handleSelectCollection} onBack={handleBack} />
        ) : view === 'collection' ? (
          <CollectionRecipeList
            collection={selectedCollection}
            onSelectRecipe={handleSelectRecipe}
            onBack={handleShowCollections}
          />
        ) : view === 'visitor-picks' ? (
          <CategoryRecipeList
            category={null}
            visitorPicksOnly={true}
            onSelectRecipe={handleSelectRecipe}
            onBack={handleBack}
          />
        ) : view === 'about' ? (
          <AboutPage onBack={handleBack} />
        ) : view === 'category' ? (
          <CategoryRecipeList
            category={selectedCategory}
            onSelectRecipe={handleSelectRecipe}
            onBack={handleBack}
          />
        ) : (
          <CategoryPage
            onSelectCategory={handleSelectCategory}
            onSelectRecipe={handleSelectRecipe}
            onShowAllRecipes={handleShowAllRecipes}
            onShowFavourites={handleShowFavourites}
            onSelectCollection={handleSelectCollection}
            onShowSwipe={handleShowSwipe}
            onShowVisitorPicks={handleShowVisitorPicks}
          />
        )}
      </div>

      {view !== 'swipe' && <FloatingAddButton onOpen={() => setShowImport(true)} />}

      {showImport && (
        <ImportPanel
          onClose={() => setShowImport(false)}
          onRecipeSaved={handleRecipeSaved}
        />
      )}

      {showEbookExport && (
        <EbookExportModal onClose={() => setShowEbookExport(false)} />
      )}
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
